import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "react-toastify";
import { signInWithPopup } from "firebase/auth";
import { signupUserThunk, firebaseLoginThunk } from "../../store/slices/authThunks";
import { auth, googleProvider, githubProvider } from "../../firebaseConfig";
import loginIllustration from "../../images/Learning language with friend.png";
import logo from "../../images/Nkenne Ai-01 2.png";
import { getStaticUrl } from "../../config/domains";
import Turnstile from "react-turnstile";

const Register = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, isAuthenticated, role } = useSelector((state) => state.auth);
  const turnstileSiteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Handle redirect if user is already authenticated (same as Login)
  useEffect(() => {
    if (isAuthenticated) {
      // Check URL params first (for cross-domain redirects from static site)
      const urlParams = new URLSearchParams(location.search);
      const redirectParam = urlParams.get('redirect');
      const planParam = urlParams.get('plan');

      if (redirectParam) {
        // Parse plan from URL if exists
        let plan = null;
        if (planParam) {
          try {
            plan = JSON.parse(decodeURIComponent(planParam));
          } catch (e) {
            console.error('Error parsing plan from URL:', e);
          }
        }

        navigate(redirectParam, {
          state: plan ? { plan } : {},
          replace: true
        });
        return;
      }

      // Check if there's a redirect path from location state
      const redirectTo = location.state?.redirectTo;

      if (redirectTo) {
        navigate(redirectTo, {
          state: {
            selectedPlan: location.state?.selectedPlan
          }
        });
      } else {
        const redirectPath = '/user-dashboard';
        navigate(redirectPath, { replace: true });
      }
    }
  }, [isAuthenticated, role, navigate, location.state, location.search]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = "First name must be at least 2 characters";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    } else if (formData.lastName.trim().length < 2) {
      newErrors.lastName = "Last name must be at least 2 characters";
    }

    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else {
      // Strong password requirements
      const minLength = formData.password.length >= 8;
      const hasUpperCase = /[A-Z]/.test(formData.password);
      const hasLowerCase = /[a-z]/.test(formData.password);
      const hasNumbers = /\d/.test(formData.password);
      const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

      if (!minLength) {
        newErrors.password = "Password must be at least 8 characters";
      } else if (!hasUpperCase) {
        newErrors.password = "Password must contain at least one uppercase letter";
      } else if (!hasLowerCase) {
        newErrors.password = "Password must contain at least one lowercase letter";
      } else if (!hasNumbers) {
        newErrors.password = "Password must contain at least one number";
      } else if (!hasSpecialChar) {
        newErrors.password = "Password must contain at least one special character (!@#$%^&*(),.?\":{}|<>)";
      }
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = "Passwords do not match";
    }

    if (!turnstileToken) {
      newErrors.turnstile = "Please complete the security check";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validateForm()) {
      return;
    }

    try {
      const result = await dispatch(signupUserThunk({
        firstName: formData.firstName.trim(),
        lastName: formData.lastName.trim(),
        email: formData.email.trim(),
        password: formData.password,
        role: "USER",
        turnstileToken: turnstileToken
      }));

      if (signupUserThunk.fulfilled.match(result)) {
        // Check if email verification is required
        if (result.payload?.requiresVerification) {
          toast.success("Account created successfully! Please check your email for verification code.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });

          // Redirect to verification page with email
          navigate("/verify-email", {
            state: { email: formData.email.trim() }
          });
        } else {
          // Old flow - direct login (backward compatibility)
          toast.success("Account created successfully! Welcome to NKENNEAi!", {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          });

          const redirectPath = result.payload.role === 'admin' ? '/admin-dashboard' : '/user-dashboard';
          if (redirectPath === '/user-dashboard') {
            navigate(redirectPath, { state: { showWelcomeModal: true, showOnboarding: true } });
          } else {
            navigate(redirectPath);
          }
        }

        // Clear form
        setFormData({
          firstName: "",
          lastName: "",
          email: "",
          password: "",
          confirmPassword: "",
        });
      } else if (signupUserThunk.rejected.match(result)) {
        toast.error(result.payload || "Signup failed. Please try again.", {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error("Signup error:", error);
      toast.error("Signup failed. Please try again.", {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  // Handle social login (Google & GitHub)
  const handleSocialLogin = async (provider, providerName) => {
    try {
      // Step 1: Sign in with Firebase
      const result = await signInWithPopup(auth, provider);
      const user = result.user;

      // Step 2: Get Firebase ID token
      const idToken = await user.getIdToken();

      // Step 3: Send token to backend for verification
      const loginResult = await dispatch(firebaseLoginThunk(idToken));

      if (firebaseLoginThunk.fulfilled.match(loginResult)) {
        const { isNewUser } = loginResult.payload;

        toast.success(
          isNewUser
            ? `Welcome to NKENNEAi! Account created successfully with ${providerName}.`
            : `Welcome back! You already have an account with ${providerName}.`,
          {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
          }
        );

        const redirectPath =
          loginResult.payload.role === "admin" ? "/admin-dashboard" : "/user-dashboard";

        // If this is a new social signup, show the welcome modal and onboarding section on dashboard
        if (isNewUser && redirectPath === "/user-dashboard") {
          navigate(redirectPath, { state: { showWelcomeModal: true, showOnboarding: true } });
        } else {
          navigate(redirectPath);
        }
      } else if (firebaseLoginThunk.rejected.match(loginResult)) {
        toast.error(loginResult.payload || `Failed to sign up with ${providerName}. Please try again.`, {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
        });
      }
    } catch (error) {
      console.error(`${providerName} signup error:`, error);

      // Handle specific Firebase errors
      let errorMessage = `Failed to sign up with ${providerName}. Please try again.`;

      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign up cancelled. Please try again.';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup was blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        errorMessage = 'An account with this email already exists. Please use your original sign-in method.';
      } else if (error.message === 'Network Error') {
        errorMessage = 'Network error. Please check your connection.';
      }

      toast.error(errorMessage, {
        position: "top-right",
        autoClose: 4000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
      });
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Header with Logo and Back Button */}
      <div className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-4 md:p-6">
        {/* Logo */}
        <div onClick={() => window.open(getStaticUrl('/'), '_blank', 'noopener,noreferrer')} className="flex items-center bg-white rounded-lg px-3 py-2 shadow-sm cursor-pointer">
          <img src={logo} alt="NkenneAi Logo" className="w-full h-8 md:h-10" />
        </div>
        {/* Back Button */}
        <button
          onClick={() => window.open(getStaticUrl('/'), '_blank', 'noopener,noreferrer')}
          className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 hover:text-gray-900 transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Website
        </button>
      </div>

      {/* Left Side - Illustration */}
      <div className="md:flex-1 bg-secondary flex items-center justify-center p-4 md:p-8 hidden md:flex">
        <img
          src={loginIllustration}
          alt="Illustration"
          className="max-w-[70%] h-auto"
        />
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 bg-white flex items-center justify-center p-4 md:p-8 pt-20 md:pt-24">
        <div className="w-full max-w-md space-y-6">
          {/* Logo and Branding */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <img src={logo} alt="Logo" className=" w-32 md:w-50 md:h-50" />
            </div>
            <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
              Welcome to NKENNEAi
            </h1>
          </div>

          <div className="space-y-6 border p-6 rounded-xl">
            <form className="space-y-4" onSubmit={handleSubmit}>
              {/* First Name Input */}
              <div>
                <label
                  htmlFor="firstName"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  First Name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  value={formData.firstName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.firstName ? "border-red-500" : "border-gray-300"
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-[#075038] focus:border-transparent text-sm`}
                  placeholder="Enter your first name"
                />
                {errors.firstName && (
                  <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
                )}
              </div>

              {/* Last Name Input */}
              <div>
                <label
                  htmlFor="lastName"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Last Name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  value={formData.lastName}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.lastName ? "border-red-500" : "border-gray-300"
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-[#075038] focus:border-transparent text-sm`}
                  placeholder="Enter your last name"
                />
                {errors.lastName && (
                  <p className="text-red-500 text-xs mt-1">{errors.lastName}</p>
                )}
              </div>

              {/* Email Input */}
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`w-full px-3 py-2 border ${errors.email ? "border-red-500" : "border-gray-300"
                    } rounded-xl focus:outline-none focus:ring-2 focus:ring-[#075038] focus:border-transparent text-sm`}
                  placeholder="Enter your email"
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Password Input */}
              <div>
                <label
                  htmlFor="password"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Password
                </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border ${errors.password ? "border-red-500" : "border-gray-300"
                      } rounded-xl focus:outline-none focus:ring-2 focus:ring-[#075038] focus:border-transparent text-sm`}
                    placeholder="Enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                )}

              </div>

              {/* Confirm Password Input */}
              <div>
                <label
                  htmlFor="confirmPassword"
                  className="block text-sm font-bold text-gray-700 mb-1"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border ${errors.confirmPassword ? "border-red-500" : "border-gray-300"
                      } rounded-xl focus:outline-none focus:ring-2 focus:ring-[#075038] focus:border-transparent text-sm`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">
                    {errors.confirmPassword}
                  </p>
                )}
              </div>

              {/* Turnstile Check */}
              <div className="flex flex-col items-center justify-center p-2">
                <Turnstile
                  sitekey={turnstileSiteKey}
                  onVerify={(token) => setTurnstileToken(token)}
                  onExpire={() => setTurnstileToken(null)}
                  onError={() => {
                    setTurnstileToken(null);
                    toast.error("Security check failed. Please refresh the page.");
                  }}
                />
                {errors.turnstile && (
                  <p className="text-red-500 text-xs mt-1">{errors.turnstile}</p>
                )}
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className={`w-full bg-secondary  hover:bg-[#075038]/90 text-white font-bold py-3 px-4 rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-[#075038] focus:ring-offset-2 text-sm flex items-center justify-center gap-2 ${loading ? "opacity-50 cursor-not-allowed" : ""
                  }`}
              >
                {loading && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                )}
                {loading ? "Signing up..." : "Sign Up"}
              </button>

            </form>

            {/* Divider */}
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500 font-bold">OR</span>
              </div>
            </div>

            {/* Social Login Buttons */}
            <div className="space-y-3">
              {/* Google Button */}
              <button
                onClick={() => handleSocialLogin(googleProvider, 'Google')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#075038] focus:ring-offset-2 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                <svg className="w-4 h-4 md:w-5 md:h-5" viewBox="0 0 24 24">
                  <path
                    fill="#4285F4"
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  />
                  <path
                    fill="#34A853"
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  />
                  <path
                    fill="#FBBC05"
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  />
                  <path
                    fill="#EA4335"
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  />
                </svg>
                Continue with Google
              </button>

              {/* GitHub Button */}
              <button
                onClick={() => handleSocialLogin(githubProvider, 'GitHub')}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-xl text-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-[#075038] focus:ring-offset-2 text-sm font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                <svg
                  className="w-4 h-4 md:w-5 md:h-5"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
                </svg>
                Continue with Github
              </button>
            </div>

            {/* Sign In Link */}
            <p className="text-center text-sm text-gray-600 font-bold">
              Already have an account?{" "}
              <Link
                to="/login"
                className="text-secondary hover:text-teal-500 font-bold"
              >
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;